import React, { useState, useEffect, useRef, useCallback } from "react";
import { Drawer, message, Image, Spin, Button, Space, Input, Tooltip, Popover } from "antd";
import DOMPurify from "dompurify";
import { serverFetch, useGetIsPhone } from "@extensions";
import { useUser, useCompany } from "@redux";
import { Auth } from "@components";
import CatalogSaleDates from "./catalog-sale-dates";
import CatalogSaleUsers from "./catalog-sale-users";
import CatalogSaleRooms from "./catalog-sale-rooms";
import CatalogSaleTime from "./catalog-sale-time";
import CatalogSaleGroups from "./catalog-sale-groups";
import CatalogSaleClients from "./catalog-sale-clients";
import ChildModal from "@components/family/child-modal";
import { getMissingClientFields } from "@components/family/profile-fields";
import { trainerSelectionRequirement } from "@extensions/enums";
import "./catalog-sale.css";

const CatalogSale = ({ item, open, onClose, onPaymentOpen, onPurchaseSuccess, initialGroupRecord = null, mode = "drawer", onOpenAccountModal }) => {
  const isPhone = useGetIsPhone();
  const user = useUser();
  const company = useCompany();
  const [profileValidating, setProfileValidating] = useState(false);
  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedGroupRecord, setSelectedGroupRecord] = useState(null);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(false);
  const [giftData, setGiftData] = useState({
    email: "",
    name: "",
    message: "",
    sign: "",
  });
  const [availableTrainersCount, setAvailableTrainersCount] = useState(0);
  // Выбранные покупатели (группа)
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  // Загруженные покупатели: сам клиент и участники группы
  const [buyerClients, setBuyerClients] = useState([]);
  // Счётчик перечитывания покупателей: растёт после правки данных участника группы
  const [buyerRefreshKey, setBuyerRefreshKey] = useState(0);
  // Участник группы, у которого не заполнены обязательные поля
  const [invalidChild, setInvalidChild] = useState(null);
  // Показывается ли блок "Для кого покупаем": без группы он не нужен, покупка оформляется на самого клиента
  const [buyerPickerVisible, setBuyerPickerVisible] = useState(false);
  // Причина блокировки кнопки "Купить" открывается на телефоне по тапу
  const [buyReasonOpen, setBuyReasonOpen] = useState(false);
  const isEmailValid = (email) => /^\S+@\S+\.\S+$/.test((email || "").trim());
  const isMounted = useRef(true);

  // Первый выбранный участник группы с незаполненными обязательными полями
  const findInvalidBuyer = useCallback(() => {
    if (!company?.requireFormForWidget) return null;
    // Первый элемент - сам клиент, остальные - члены его группы
    const [, ...children] = buyerClients;
    // Сам клиент проверяется в validateProfile, здесь - только дети
    const selectedChildren = children.filter(c => selectedClientIds.includes(c.id));
    return selectedChildren.find(c => getMissingClientFields(c, company).length > 0) || null;
  }, [buyerClients, selectedClientIds, company]);

  // Для recordVid=1 тренер всегда обязателен
  // Для абонементов/пакетов (vid=2,3) требование тренера зависит от trainerSelectionRequirement
  // Если тренеров нет, требование снимается
  const isTrainerRequired = product?.recordVid === 1 ||
                            ((product?.vid === 2 || product?.vid === 3) &&
                            (product?.trainerSelectionRequirement === trainerSelectionRequirement.ClientChoose ||
                            product?.trainerSelectionRequirement === trainerSelectionRequirement.Both) &&
                            availableTrainersCount > 0);

  const buyerCount = Math.max(selectedClientIds.length, 1);
  const totalPrice = (item?.price || 0) * buyerCount;
  const isMultiBuyer = buyerCount > 1;

  // Мест в групповой записи может не хватить на всех выбранных участников:
  // сервер это тоже проверяет, но кнопку блокируем заранее
  const groupSlotsAvailable = product?.recordVid === 3 ? selectedGroupRecord?.availableCount : null;
  const groupSlotsShort = groupSlotsAvailable != null && selectedClientIds.length > groupSlotsAvailable;

  // Причина блокировки кнопки "Купить"
  const buyDisabledReason = (() => {
    if (loadingPurchase) return "Оформляем покупку...";
    if (profileValidating) return "Проверяем заполнение анкеты...";
    // Без группы выбирать некого: покупаем на себя
    if (buyerPickerVisible && selectedClientIds.length === 0) return "Выберите, для кого покупаем";
    if (groupSlotsShort) {
      return `В группе осталось мест: ${groupSlotsAvailable}, а выбрано участников: ${selectedClientIds.length}`;
    }
    if (product?.recordVid === 1 && (!selectedSession?.date || !selectedTime || !selectedUser)) {
      return "Выберите дату, время и тренера";
    }
    if ((product?.vid === 2 || product?.vid === 3) && isTrainerRequired && !selectedUser) {
      return "Выберите тренера";
    }
    if (product?.recordVid === 2 && (!selectedSession?.date || !selectedRoom || !selectedTime)) {
      return "Выберите дату, время и зал";
    }
    if (product?.recordVid === 3 && !selectedGroupRecord) {
      return "Выберите групповое занятие";
    }
    if (product?.vid === 4 && !giftData.email.trim()) {
      return "Укажите email получателя сертификата";
    }
    return "";
  })();

  // На телефоне Tooltip antd завязан на наведение, поэтому причину
  // показываем по тапу. Состоянием управляем сами: у заблокированной
  // кнопки события не доходят до триггера antd
  useEffect(() => {
    setBuyReasonOpen(false);
  }, [buyDisabledReason]);

  const validateProfile = useCallback(() => {
    setProfileValidating(true);
    return serverFetch("OnlineClient/info")
      .then((client) => {
        if (!company?.requireFormForWidget) {
          return { isValid: true };
        }
        const missingFields = [];
        if (company.requiredClientProfileFields & 1) {
          const name = client?.name || "";
          if (!name || name.trim() === "" || /^\+?\d+$/.test(name.trim())) {
            missingFields.push("ФИО");
          }
        }
        if (company.requiredClientProfileFields & 2) {
          if (!client.dateBirth) {
            missingFields.push("Дата рождения");
          }
        }
        if (company.requiredClientProfileFields & 4) {
          // Пол: 0 - женщина, поэтому проверяем именно null/undefined
          if (client.sex === null || client.sex === undefined) {
            missingFields.push("Пол");
          }
        }
        if (company.requiredClientProfileFields & 8) {
          const email = client?.email || "";
          if (!email || email.trim() === "") {
            missingFields.push("Email");
          }
        }
        return {
          isValid: missingFields.length === 0,
          errors: missingFields
        };
      })
      .catch((e) => {
        console.error("Ошибка проверки профиля клиента:", e);
        return { isValid: true };
      })
      .finally(() => {
        setProfileValidating(false);
      });
  }, [company]);

  useEffect(() => {
    if (open && item?.productId) {
      setSelectedSession(null);
      setSelectedUser(null);
      setSelectedRoom(null);
      setSelectedTime(null);
      setSelectedGroupRecord(initialGroupRecord);
      setAvailableTrainersCount(0);
      // Сбрасываем выбор покупателей
      setSelectedClientIds([]);
      setBuyerPickerVisible(false);
      setLoadingProduct(true);
      serverFetch(`Online/Product/${item.productId}`)
        .then((data) => {
          setProduct(data);
          setLoadingProduct(false);
        })
        .catch((error) => {
          message.error(error?.UserMessage || "Не удалось загрузить информацию о продукте");
          setLoadingProduct(false);
        });
    } else {
      setProduct(null);
      setSelectedSession(null);
      setSelectedUser(null);
      setSelectedUserId(null);
      setSelectedRoom(null);
      setSelectedTime(null);
      setSelectedGroupRecord(null);
      setAvailableTrainersCount(0);
      // Сбрасываем выбор покупателей
      setSelectedClientIds([]);
      setBuyerPickerVisible(false);
      setGiftData({
        email: "",
        name: "",
        message: "",
        sign: "",
      });
    }
  }, [open, item?.productId, initialGroupRecord]);

  useEffect(() => {
    if (!open || product?.vid !== 4 || !user) {
      return;
    }

    isMounted.current = true;
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((client) => {
        if (!isMounted.current) { return; }
        setGiftData((prev) => ({
          email: prev.email || client?.email || "",
          name: prev.name || client?.name || "",
          message: prev.message || "",
          sign: prev.sign || "",
        }));
      })
      .catch(() => {});

    return () => {
      isMounted.current = false;
    };
  }, [open, product?.vid, user]);

  // Продолжение покупки после авторизации
  useEffect(() => {
    if (pendingPurchase && user) {
      setPendingPurchase(false);
      setShowSignin(false);
      // Вызываем performPurchase через небольшую задержку, чтобы убедиться, что user обновлен
      setTimeout(() => {
        performPurchase();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPurchase, user]);

  const performPurchase = async () => {
    if (!item || !user) { return; }
    
    // Проверка для услуг с записью к тренеру
    if (product?.recordVid === 1 && (!selectedSession?.date || !selectedTime)) {
      message.error("Необходимо выбрать дату и время");
      return;
    }
    // Проверка наличия тренера
    if (isTrainerRequired && !selectedUser) {
      message.error("Необходимо выбрать сотрудника");
      return;
    }
    if (product?.recordVid === 2 && (!selectedSession?.date || !selectedRoom || !selectedTime)) {
      message.error("Необходимо выбрать дату, зал и время");
      return;
    }
    if (product?.recordVid === 3 && !selectedGroupRecord) {
      message.error("Необходимо выбрать группу");
      return;
    }
    if (product?.vid === 4 && !giftData.email.trim()) {
      message.error("Поле \"Почта\" обязательно для заполнения");
      return;
    }
    if (product?.vid === 4 && !isEmailValid(giftData.email)) {
      message.error("Введите корректный email");
      return;
    }

    setLoadingPurchase(true);

    try {
      const formatTime = (timeStr) => {
        if (!timeStr) return timeStr;
        if (timeStr.length === 5) {
          return `${timeStr}:00`;
        }
        if (!timeStr.includes(":")) {
          return `${timeStr.padStart(2, "0")}:00:00`;
        }
        return timeStr;
      };
      const requestData = { catalogItemId: item.catalogId || item.id, };
      // Перечисляем покупателей: покупка создаётся для каждого из них
      if (selectedClientIds.length > 0) {
        requestData.clientIds = selectedClientIds;
      }
      if (product?.recordVid === 1 || product?.recordVid === 2) {
        const dateStr = selectedSession.date.format("YYYY-MM-DD");
        const dateTimeStr = `${dateStr}T00:00:00`;
        requestData.date = dateTimeStr;
        const timeStr = formatTime(selectedTime);
        requestData.time = timeStr;
        if (product?.recordVid === 1) {
          requestData.userId = selectedUser.id;
        } else {
          requestData.roomId = selectedRoom.id;
        }
      }
      if (product?.recordVid === 3) {
        requestData.recordId = selectedGroupRecord.id;
      }
      // Для абонементов/пакетов передаём ID сотрудника для поля исполнителя
      if ((product?.vid === 2 || product?.vid === 3) && selectedUserId) {
        requestData.userId = selectedUserId;
      }
      if (product?.vid === 4) {
        requestData.email = giftData.email.trim();
        requestData.name = giftData.name.trim();
        requestData.message = giftData.message.trim();
        requestData.sign = giftData.sign.trim();
      }

      const result = await serverFetch(
        "OnlineClient/Sale",
        {
          method: "POST",
          bodyData: requestData,
        },
        user
      );

      // Ответ - массив результатов [{ Sale, Record }, ...] (по одному на каждого покупателя).
      // Поддерживаем и прежнюю форму { Sale, Record } для совместимости.
      const results = Array.isArray(result) ? result : [result];
      const createdSales = results
        .filter(r => r?.sale)
        .map(r => r.productName ? { ...r.sale, productName: r.productName } : r.sale);
      const hasRecords = results.some(r => r?.record);

      if (createdSales.length > 0) {
        message.success(createdSales.length > 1 ? "Покупки успешно оформлены!" : "Покупка успешно оформлена!");
        // Передаём все созданные продажи, а не только первую
        if (onPurchaseSuccess) {
          onPurchaseSuccess(createdSales);
          return;
        }
        onClose();
        if (onPaymentOpen) {
          onPaymentOpen(createdSales);
        }
      } else if (hasRecords) {
        message.success("Вы успешно записаны!");
        onClose();
      } else {
        message.error("Не удалось оформить покупку");
      }
    } catch (error) {
      console.error("Ошибка при оформлении покупки:", error);
      message.error(error?.userMessage || error?.message || "Не удалось оформить покупку");
    } finally {
      setLoadingPurchase(false);
    }
  };

  const handlePurchase = () => {
    if (!item) {
      return;
    }

    if (!user) {
      setShowSignin(true);
      setPendingPurchase(true);
      return;
    }

    // Проверяем анкеты выбранных клиентов - сначала представителя, если он выбран, потом другие участники группы
    const selfClient = buyerClients[0];
    const buyingExclusivelyForGroup = selectedClientIds.length > 0 && !selectedClientIds.includes(selfClient?.id);

    if (buyingExclusivelyForGroup) {
      const childWithMissingFields = findInvalidBuyer();
      if (childWithMissingFields) {
        message.warning(`Заполните данные участника группы: ${getMissingClientFields(childWithMissingFields, company).join(", ")}`);
        setInvalidChild(childWithMissingFields);
        return;
      }
      performPurchase();
      return;
    }

    validateProfile().then((validationResult) => {
      if (!validationResult.isValid) {
        const action = {
          type: 'catalog',
          item: item,
          autopay: item.autopay || false
        };
        if (onOpenAccountModal) {
          onOpenAccountModal(action, validationResult);
        }
        return;
      }

      // Дети проверяются так же, как сам клиент: без заполненных полей покупать нельзя
      const childWithMissingFields = findInvalidBuyer();
      if (childWithMissingFields) {
        message.warning(`Заполните данные участника группы: ${getMissingClientFields(childWithMissingFields, company).join(", ")}`);
        setInvalidChild(childWithMissingFields);
        return;
      }

      performPurchase();
    }).catch((e) => {
      console.error("Ошибка проверки профиля клиента:", e);
      performPurchase();
    });
  };

  const handleDateSelect = (session) => {
    setSelectedSession(session);
    setSelectedUser(null);
    setSelectedUserId(null);
    setSelectedRoom(null);
    setSelectedTime(null);
  };

  const handleUserIdSelect = (selected) => {
    setSelectedUserId(selected ? selected.id : null);
    setSelectedUser(selected);
  };

  const handleRoomSelect = (selected) => {
    setSelectedRoom(selected);
    setSelectedTime(null);
  };

  // Состав приходит из списка занятий, поэтому занятые видны ещё до выбора.
  const [bookedByRecord, setBookedByRecord] = useState({});
  const recordBookedClientIds = bookedByRecord[selectedGroupRecord?.id] ?? [];


  // Кнопка "Купить" с подсказкой причины блокировки.
  const buyButton = isPhone ? (
    <Popover
      content={buyDisabledReason}
      open={buyReasonOpen && Boolean(buyDisabledReason)}
      onOpenChange={setBuyReasonOpen}
      placement="top"
      overlayClassName="catalog-sale-buy-reason"
    >
      <span className="catalog-sale-button-wrap" 
        onClick={() => {
          if (buyDisabledReason) {
            setBuyReasonOpen(true);
          }
        }}
      >
        <Button
          className="catalog-sale-button"
          type="primary"
          disabled={Boolean(buyDisabledReason)}
          loading={loadingPurchase}
          onClick={handlePurchase}
        >Купить</Button>
      </span>
    </Popover>
  ) : (
    <Tooltip title={buyDisabledReason} placement="top" overlayClassName="catalog-sale-tooltip">
      <span className="catalog-sale-button-wrap">
        <Button
          className="catalog-sale-button"
          type="primary"
          disabled={Boolean(buyDisabledReason)}
          loading={loadingPurchase}
          onClick={handlePurchase}
        >Купить</Button>
      </span>
    </Tooltip>
  );

  const saleContent = (
    <>
      {loadingProduct ? (
        <div className="catalog-sale-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {product?.pictureId && (
            <div className="catalog-sale-image-container">
              <Image
                src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${product.pictureId}`}
                alt={item?.name}
                preview={false}
                className="catalog-sale-image"
              />
            </div>
          )}
          {product?.description && (
            <div className="catalog-sale-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(product.description, {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6"],
                  }),
                }}
              />
            </div>
          )}
          {product?.vid !== 4 && (
            <CatalogSaleClients
              selectedIds={selectedClientIds}
              onChange={setSelectedClientIds}
              onClientsLoaded={setBuyerClients}
              refreshKey={buyerRefreshKey}
              bookedClientIds={recordBookedClientIds}
              onGroupVisibilityChange={setBuyerPickerVisible}
              vid={product?.vid}
              recordVid={product?.recordVid}
            />
          )}
          {item?.price && (
            <div className="catalog-sale-price">
              {isMultiBuyer ? (
                <>
                  {totalPrice.toLocaleString()} руб. <span className="catalog-sale-price-multiplier">({item.price.toLocaleString()} × {buyerCount})</span>
                </>
              ) : (
                `${item.price.toLocaleString()} руб.`
              )}
            </div>
          )}
          {product?.recordVid === 1 && (
            <>
              <CatalogSaleDates onSelect={handleDateSelect} />
              {selectedSession?.date && (
                <CatalogSaleUsers
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={handleUserIdSelect}
                  isRequired={true}
                  trainerSelectionRequirement={product?.trainerSelectionRequirement}
                  selectedUser={selectedUser}
                />
              )}
              {selectedUser && selectedSession?.date && (
                <CatalogSaleTime
                  userId={selectedUser.id}
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={setSelectedTime}
                />
              )}
            </>
          )}
          {product?.recordVid === 2 && (
            <>
              <CatalogSaleDates onSelect={handleDateSelect} />
              {selectedSession?.date && (
                <CatalogSaleRooms
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={handleRoomSelect}
                />
              )}
              {selectedRoom && selectedSession?.date && (
                <CatalogSaleTime
                  roomId={selectedRoom.id}
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={setSelectedTime}
                />
              )}
            </>
          )}
          {product?.recordVid === 3 && (
            <CatalogSaleGroups
              productId={item?.productId}
              onSelect={setSelectedGroupRecord}
              initialGroupRecord={initialGroupRecord}
              selectedClientIds={selectedClientIds}
              onBookedChange={setBookedByRecord}
            />
          )}
          {(product?.vid === 2 || product?.vid === 3) && product?.recordVid !== 3 && (
            <CatalogSaleUsers
              productId={item?.productId}
              onSelect={handleUserIdSelect}
              isRequired={product?.trainerSelectionRequirement === trainerSelectionRequirement.ClientChoose ||
                           product?.trainerSelectionRequirement === trainerSelectionRequirement.Both}
              trainerSelectionRequirement={product?.trainerSelectionRequirement}
              selectedUser={selectedUserId ? { id: selectedUserId } : null}
              onUsersLoaded={setAvailableTrainersCount}
             />
            )}
          {product?.vid === 4 && (
            <div className="catalog-sale-gift-fields">
              <Input
                placeholder="Почта"
                value={giftData.email}
                onChange={(e) => setGiftData((prev) => ({ ...prev, email: e.target.value }))}
                status={!giftData.email.trim() || isEmailValid(giftData.email) ? "" : "error"}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Получатель"
                value={giftData.name}
                onChange={(e) => setGiftData((prev) => ({ ...prev, name: e.target.value }))}
                style={{ marginBottom: 8 }}
              />
              <Input.TextArea
                placeholder="Сообщение"
                value={giftData.message}
                onChange={(e) => setGiftData((prev) => ({ ...prev, message: e.target.value }))}
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Подпись"
                value={giftData.sign}
                onChange={(e) => setGiftData((prev) => ({ ...prev, sign: e.target.value }))}
                style={{ marginBottom: 8 }}
              />
            </div>
          )}
          <Space className="catalog-sale-buttons">
            <Button onClick={onClose} className="catalog-sale-button">Назад</Button>
            {buyButton}
          </Space>
        </>
      )}
      {showSignin && <Auth open={true} onClose={() => { setShowSignin(false); setPendingPurchase(false); }} />}
      {invalidChild && (
        <ChildModal
          open={true}
          child={invalidChild}
          onClose={() => setInvalidChild(null)}
          onSaved={() => {
            setInvalidChild(null);
            setBuyerRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );

  if (mode === "page") {
    return (
      <div className={`catalog-sale-page-card ${product?.pictureId ? "" : "catalog-sale-page-card_no-image"}`}>
        <div className="catalog-sale-page-title">{item?.name}</div>
        {saleContent}
      </div>
    );
  }
  return (
    <Drawer
      title={item?.name}
      placement="right"
      onClose={onClose}
      open={open}
      width={isPhone ? "100%" : 540}
      closable={true}
      className="catalog-sale-drawer"
      styles={{ body: { padding: isPhone ? "8px 8px 8px 8px" : "0px 16px 16px 16px", paddingTop: 0 } }}
    >
      {saleContent}
    </Drawer>
  );
};

export default CatalogSale;
